import { CommonModule } from '@angular/common';
import {
	HTTP_INTERCEPTORS,
	HttpClient,
	HttpClientModule,
} from '@angular/common/http';
import {
	APP_INITIALIZER,
	ApplicationConfig,
	importProvidersFrom,
	inject,
	InjectionToken,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	provideRouter,
	withComponentInputBinding,
	withViewTransitions,
} from '@angular/router';
import { DcBaseInitializationService } from '@datachain/ui-sdk/common';
import { AppIcons, AppModalService } from '@datachain/ui-sdk/components';
import { provideEffects } from '@ngrx/effects';
import { ActionReducerMap, provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { ProfileAdapter } from './core/adapters/profile.adapter';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { AppEffects, CORE, coreReducer, IAppRootState } from './core/store';
import { getDetailsForConnectedUser } from './core/store/app.actions';
import { IProfileModel } from './requesters/profile/profile.model';
import { AppActionsIcons } from './ui/app-actions.icons';
import { AppUiComponentIcons } from './ui/app-components.icons';
import { AppDcMarketPlaceIcons } from './ui/app-marketplace-icons';
import { environment } from '../environments/environment';

export const AppMarketPlaceIconsInjectable = new InjectionToken(
	'dc-marketplace icons default namespace',
	{
		factory: (): AppIcons =>
			new AppDcMarketPlaceIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	},
);

export const UiComponentIconsInjectable = new InjectionToken(
	'ADOBIS UI components data namespace',
	{
		factory: (): AppIcons =>
			new AppUiComponentIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	},
);

export const ActionIconsInjectable = new InjectionToken(
	'Actions data namespace',
	{
		factory: (): AppIcons =>
			new AppActionsIcons(inject(MatIconRegistry), inject(DomSanitizer)),
	},
);

export const APP_INIT =
	(
		http: HttpClient,
		store: Store,
		profileAdapter: ProfileAdapter,
	): (() => Promise<unknown>) =>
	async () => {
		const isAuthenticated = await firstValueFrom(
			http.get('authenticated', {
				responseType: 'text' as const,
			}),
		);
		const paths = await firstValueFrom(
			http.get<Record<string, string>>('private-dmp-api/apiPaths'),
		);
		const config = DcBaseInitializationService.getConfigInstance();
		DcBaseInitializationService.updateConfig(
			config.setAvailableResources(paths),
		);

		if (isAuthenticated) {
			const userAccount = await firstValueFrom(
				http.get<IProfileModel>('/account'),
			);

			const connectedUser = profileAdapter.generateUserProfile(userAccount);

			store.dispatch(
				getDetailsForConnectedUser({
					preferredLang: userAccount.preferences.language,
					user: connectedUser,
				}),
			);
		}
	};

const APP_INTERCEPTORS = [
	{
		provide: HTTP_INTERCEPTORS,
		useClass: AuthInterceptor,
		multi: true,
	},
];

const rootReducer: ActionReducerMap<IAppRootState> = {
	[CORE]: coreReducer,
};

export const appConfig: ApplicationConfig = {
	providers: [
		AppModalService,
		...APP_INTERCEPTORS,
		{
			provide: APP_INITIALIZER,
			useFactory: APP_INIT,
			deps: [HttpClient, Store, ProfileAdapter],
			multi: true,
		},
		importProvidersFrom(CommonModule, HttpClientModule),
		provideAnimations(),
		provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
		provideStore(rootReducer),
		provideEffects(AppEffects),
		provideStoreDevtools({
			maxAge: 25,
			logOnly: environment.production,
			autoPause: true,
			trace: environment.enableDebug,
			traceLimit: 75,
			connectInZone: true,
		}),
	],
};
